/**
 * Per-viewer "analyse as I browse" switch.
 *
 * A browser preference, not account state: it depends on the device's CPU and the
 * user's battery, so it deliberately does not sync across devices and never reaches
 * the API. Storage access throws outright in some privacy modes, so every read and
 * write is guarded and falls back to the in-memory default.
 */

import { useSyncExternalStore } from 'react';

export const LOCAL_ANALYSIS_KEY = 'chessreps.localAnalysis';

/**
 * In-memory copy, so a switch flipped where storage is unavailable still reaches
 * every panel in this tab; `null` until the first write.
 */
let override: boolean | null = null;
const listeners = new Set<() => void>();

export function readLocalAnalysisPreference(
  storage: Pick<Storage, 'getItem'> | null = safeStorage(),
  fallback = true,
): boolean {
  try {
    const raw = storage?.getItem(LOCAL_ANALYSIS_KEY);
    if (raw === null || raw === undefined) return fallback;
    return raw === '1';
  } catch {
    return fallback;
  }
}

export function writeLocalAnalysisPreference(
  value: boolean,
  storage: Pick<Storage, 'setItem'> | null = safeStorage(),
): void {
  override = value;
  try {
    storage?.setItem(LOCAL_ANALYSIS_KEY, value ? '1' : '0');
  } catch {
    // A viewer who cannot persist the switch still gets it for this session.
  }
  for (const listener of [...listeners]) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function readCurrent(): boolean {
  return override ?? readLocalAnalysisPreference();
}

/** The switch, live: every panel follows a flip made anywhere in the tab. */
export function useLocalAnalysisPreference(): boolean {
  return useSyncExternalStore(subscribe, readCurrent, readCurrent);
}

/** Forget the in-memory copy (tests). */
export function resetLocalAnalysisPreference(): void {
  override = null;
}

function safeStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}
